import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import { getGuideAccent } from "../../utils/guideAccent";

// Template336 (Edimburgo): Día 2 Arthur’s Seat, Canongate y Calton Hill
const Template336 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const introText = page.texts?.[1]?.content;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";
	const compact = Boolean(page?.data?.compact);
	const imageHeight = compact ? 72 : 110;
	const sectionGap = compact ? "7px" : "10px";
	const boxPadding = compact ? "7px 10px" : "12px";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "18px 12px 5px",
					position: "relative",
					marginTop: 0,
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				{introText && (
					<Box
						component="div"
						dangerouslySetInnerHTML={{ __html: introText }}
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginTop: "-10px",
							padding: 0,
							"& p": { margin: "0 0 8px" },
							"& p:last-child": { marginBottom: 10 },
						}}
					/>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: sectionGap,
						flex: 1,
						minHeight: 0,
						overflow: "visible",
					}}
				>
					{sections.map((section, idx) => {
						const imageSrc = getImageSrc(section.imageId);
						const isLast = idx === sections.length - 1;

						if (
							section.layout === "box" ||
							section.layout === "boxOutline" ||
							section.layout === "yellow"
						) {
							const isOutline = section.layout === "boxOutline";
							const isYellow = section.layout === "yellow";
							const isRightBox = section.align === "right";
							const boxSticker = getCanvasImageUrl(section.stickerCanvasId);
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{
										margin: 0,
										padding: 0,
										position: "relative",
										overflow: "visible",
									}}
								>
									{isRightBox && boxSticker && (
										<>
											<img
												src={boxSticker}
												alt=""
												style={{
													width: 95,
													position: "absolute",
													left: -18,
													bottom: -8,
													zIndex: 5,
												}}
											/>
										</>
									)}
									<Box
										component="p"
										sx={{
											...estiloP,
											padding: boxPadding,
											backgroundColor: isYellow
												? "#fff8db"
												: isOutline
													? "#f7fafc"
													: accent.boxBg,
											border: isYellow
												? "2px solid #f5c400"
												: isOutline
													? `3px solid ${accent.boxBorder}`
													: "none",
											borderRadius: "12px",
											width: isRightBox ? "80%" : "100%",
											marginLeft: isRightBox ? "auto" : 0,
											boxSizing: "border-box",
										}}
									>
										{section.title && (
											<Box component="h3" sx={estiloH3}>
												{section.title}
											</Box>
										)}
										{section.body}
									</Box>
								</Box>
							);
						}

						if (section.layout === "sticker") {
							const stickerSrc = getCanvasImageUrl(section.stickerCanvasId);
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{
										position: "relative",
										height: 54,
										margin: 0,
										padding: 0,
										overflow: "visible",
									}}
								>
									{stickerSrc && (
										<img
											src={stickerSrc}
											alt=""
											style={{
												width: 95,
												position: "absolute",
												right: -18,
												bottom: isSmallScreen ? -130 : -8,
												zIndex: 5,
											}}
										/>
									)}
									{section.bubble && (
										<Box
											sx={{
												position: "absolute",
												right: isSmallScreen ? 50 : 68,
												top: isSmallScreen ? 4 : "-20px",
												backgroundColor: "#fff8db",
												border: "2px solid #f5c400",
												borderRadius: "16px 16px 0px 16px",
												padding: "8px 10px",
												fontSize: 11,
												fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
												color: contentColor,
												boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
												width: isSmallScreen ? 140 : 190,
												zIndex: 6,
												lineHeight: 1.35,
											}}
										>
											{section.bubble}
										</Box>
									)}
								</Box>
							);
						}

						if (section.layout === "list") {
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{ margin: 0, padding: 0 }}
								>
									{section.title && (
										<Box
											component="h3"
											sx={{ ...estiloH3, marginBottom: "6px" }}
										>
											{section.title}
										</Box>
									)}
									<Box
										component="ul"
										sx={{
											...estiloP,
											paddingLeft: "18px",
											margin: 0,
										}}
									>
										{(section.listItems || []).map((item, itemIdx) => (
											<Box
												component="li"
												key={itemIdx}
												sx={{ marginBottom: "6px" }}
											>
												{item.title && <strong>{item.title}. </strong>}
												{item.body}
											</Box>
										))}
									</Box>
								</Box>
							);
						}

						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 2}`}
								sx={{
									display: "flex",
									gap: "16px",
									alignItems: "flex-start",
									position: "relative",
									overflow: "visible",
									margin: 0,
									marginTop:
										isSmallScreen && section.imageId === "calton" ? "120px" : 0,
									padding: 0,
								}}
							>
								{imageSrc && (
									<Box
										sx={{
											width: "40%",
											position: "relative",
											flexShrink: 0,
											overflow: "visible",
											lineHeight: 0,
											fontSize: 0,
										}}
									>
										<img
											src={imageSrc}
											alt={section.title}
											style={{
												width: "100%",
												height: imageHeight,
												objectFit: "cover",
												borderRadius: "12px",
												display: "block",
											}}
										/>
										{section.stickerCanvasId &&
											getCanvasImageUrl(section.stickerCanvasId) && (
												<>
													<img
														src={getCanvasImageUrl(section.stickerCanvasId)}
														alt=""
														style={{
															width: 100,
															position: "absolute",
															bottom:
																section.stickerCanvasId === "bakehouse-sticker"
																	? isSmallScreen
																		? -180
																		: -100
																	: isSmallScreen
																		? -50
																		: -20,
															left: -30,
															zIndex: 5,
														}}
													/>
													{section.bubble && (
														<Box
															sx={{
																position: "absolute",
																left:
																	section.stickerCanvasId ===
																	"bakehouse-sticker"
																		? -10
																		: 0,
																top:
																	section.stickerCanvasId ===
																	"bakehouse-sticker"
																		? isSmallScreen
																			? 120
																			: 50
																		: "auto",
																bottom:
																	section.stickerCanvasId ===
																	"bakehouse-sticker"
																		? "auto"
																		: 50,
																backgroundColor: "#fff8db",
																border: "2px solid #f5c400",
																borderRadius: "16px 16px 16px 0px",
																padding: "8px 10px",
																fontSize: 11,
																fontFamily: `${
																	fontFamily || "'Poppins', sans-serif"
																}`,
																color: contentColor,
																boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
																width:
																	section.stickerCanvasId ===
																	"bakehouse-sticker"
																		? 140
																		: 120,
																zIndex: 6,
																lineHeight: 1.35,
															}}
														>
															{section.bubble}
														</Box>
													)}
												</>
											)}
									</Box>
								)}
								{section.sabiasQue &&
									section.sabiasQue.map((sq, i) => (
										<Box
											key={i}
											sx={{
												position: "absolute",
												top: -10 + i * 38,
												left: imageSrc ? "calc(40% - 22px)" : "50%",
												transform: imageSrc ? "none" : "translateX(-50%)",
											}}
										>
											<SabiasQue
												icono={sq.icono}
												texto={sq.texto}
												position={
													section.sabiasQuePosition ||
													(isLast ? "above" : undefined)
												}
											/>
										</Box>
									))}
								<Box
									sx={{
										width: imageSrc ? "60%" : "100%",
										boxSizing: "border-box",
										paddingBottom:
											isSmallScreen && section.imageId === "calton"
												? "12px"
												: 0,
									}}
								>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body && (
										<Box
											component="p"
											sx={{ ...estiloP, fontSize: 13, color: contentColor }}
										>
											{section.body}
										</Box>
									)}
								</Box>
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
};

Template336.thumbnail = "/images/template-1.png";

export default Template336;
